import {http as httpApiGateway} from "./api/restApiClient";

// Every async "generate a file" flow (admission PDF, blank template PDF, rates PDF,
// export-audit, ...) reports its result to the same generic status endpoint,
// regardless of which Lambda produced the file — see modules/file_generation on
// the backend. Kicking generation off asynchronously (returning a jobId immediately)
// and polling for the result keeps the initial request well under API Gateway's
// 29s integration timeout, even when the generation itself takes longer.

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 45; // ~90s of polling

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Poll the generic async file-generation status endpoint until a presigned download
 * URL is available.
 *
 * A non-2xx response (e.g. the job failed server-side, surfaced as FAILED) throws
 * immediately via apisauce's response transform — the caller's try/catch handles it,
 * no need to keep polling in that case.
 */
export const pollFileGenerationStatus = async (
  jobId: string,
): Promise<string> => {
  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
    await sleep(POLL_INTERVAL_MS);

    const statusResponse = await httpApiGateway.get(
      `/files/generate/status/${jobId}`,
    );
    const downloadUrl = (statusResponse.data as {downloadUrl?: string})
      ?.downloadUrl;
    if (downloadUrl) {
      return downloadUrl;
    }
  }

  throw new Error("Timed out waiting for file generation to complete");
};
